import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  FaUsers,
  FaLayerGroup,
  FaCode,
  FaBookOpen,
  FaBell,
  FaArrowRight,
  FaArrowUp,
  FaArrowDown,
  FaRegCalendarAlt,
  FaRegClock,
  FaHourglassHalf,
} from 'react-icons/fa';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceDot,
  ResponsiveContainer,
} from 'recharts';
import Loader from '../../Loader';
import { ROUTES } from '../../../assets/constants/strings';
import { READER_APP } from '../../../services/entitlements';
import { getAdminOverview, getEnquiries } from '../../../services/axios';

const mapStateToProps = (state) => ({
  token: state.user.user ? state.user.user.token : null,
});

const connector = connect(mapStateToProps);

const TEAL = '#00a587';

const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString());
const link = (page) => `${ROUTES.APIAdmin}/${page}`;

const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return isNaN(dt)
    ? '—'
    : dt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) +
        ', ' +
        dt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
};

// Short day label ("9 Jul") for chart axes/tooltips.
const fmtDay = (d) => {
  const dt = new Date(d);
  return isNaN(dt) ? '' : dt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

// "1 – 13 Jul 2026" for the current month-to-date (the reporting period for activity stats).
const monthRangeLabel = () => {
  const now = new Date();
  const mo = now.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
  return `1 – ${now.getDate()} ${mo}`;
};

const Kpi = ({ icon, tone, value, valueText, label, sub, trend, spark }) => (
  <div className={`adm-kpi adm-kpi--${tone}`}>
    <div className='adm-kpi__head'>
      <span className={`adm-kpi__icon adm-kpi__icon--${tone}`}>{icon}</span>
      <span className='adm-kpi__label'>{label}</span>
    </div>
    <div className='adm-kpi__row'>
      <div className='adm-kpi__value'>{valueText != null ? valueText : fmt(value)}</div>
      {spark && spark.length > 1 && (
        <div className='adm-kpi__spark'>
          <ResponsiveContainer width='100%' height='100%'>
            <AreaChart data={spark} margin={{ top: 4, right: 1, bottom: 2, left: 1 }}>
              <defs>
                <linearGradient id='sparkFill' x1='0' y1='0' x2='0' y2='1'>
                  <stop offset='0%' stopColor={TEAL} stopOpacity={0.28} />
                  <stop offset='100%' stopColor={TEAL} stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area
                type='monotone'
                dataKey='count'
                stroke={TEAL}
                strokeWidth={2}
                fill='url(#sparkFill)'
                dot={false}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
    {(sub || trend) && (
      <div className='adm-kpi__foot'>
        {sub && <span className='adm-kpi__sub'>{sub}</span>}
        {trend && (
          <span className={`adm-kpi__trend adm-kpi__trend--${trend.dir}`}>
            {trend.dir === 'down' && <FaArrowDown />}
            {trend.dir === 'up' && <FaArrowUp />}
            {/* Not a movement — a deadline, so it reads as a clock rather than an arrow. */}
            {trend.dir === 'warn' && <FaRegClock />} {trend.text}
          </span>
        )}
      </div>
    )}
  </div>
);

const MiniStat = ({ tone, label, value }) => (
  <div className='adm-mini'>
    <div className='adm-mini__top'>
      <span className={`adm-mini__dot adm-mini__dot--${tone}`} />
      <span className='adm-mini__value'>{fmt(value)}</span>
    </div>
    <div className='adm-mini__label'>{label}</div>
  </div>
);

const ChartTip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  return (
    <div className='adm-charttip'>
      {p.label} · <b>{fmt(p.count)}</b> calls
    </div>
  );
};

// Hero activity chart — daily API calls over the series returned by the backend (last 30 days).
const ActivityChart = ({ data }) => {
  if (!data.length) {
    return <div className='adm-chart__empty'>No API activity to chart yet.</div>;
  }
  const last = data[data.length - 1];
  return (
    <ResponsiveContainer width='100%' height={248}>
      <AreaChart data={data} margin={{ top: 8, right: 12, bottom: 4, left: -8 }}>
        <defs>
          <linearGradient id='activityFill' x1='0' y1='0' x2='0' y2='1'>
            <stop offset='0%' stopColor={TEAL} stopOpacity={0.26} />
            <stop offset='100%' stopColor={TEAL} stopOpacity={0.02} />
          </linearGradient>
        </defs>
        <CartesianGrid vertical={false} stroke='#eef1f6' />
        <XAxis
          dataKey='label'
          tickLine={false}
          axisLine={false}
          minTickGap={40}
          tick={{ fill: '#8b98ab', fontSize: 11 }}
          dy={6}
        />
        <YAxis
          width={40}
          tickLine={false}
          axisLine={false}
          allowDecimals={false}
          tick={{ fill: '#8b98ab', fontSize: 11 }}
        />
        <Tooltip content={<ChartTip />} cursor={{ stroke: TEAL, strokeDasharray: '3 3' }} />
        <Area
          type='monotone'
          dataKey='count'
          stroke={TEAL}
          strokeWidth={2.5}
          fill='url(#activityFill)'
          dot={false}
          activeDot={{ r: 5, fill: TEAL, stroke: '#fff', strokeWidth: 2 }}
          isAnimationActive={false}
        />
        <ReferenceDot
          x={last.label}
          y={last.count}
          r={4.5}
          fill={TEAL}
          stroke='#fff'
          strokeWidth={2}
          isFront
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};

const Overview = ({ token }) => {
  const [stats, setStats] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      setLoading(true);
      const [s, e] = await Promise.all([getAdminOverview(token), getEnquiries(token)]);
      if (cancelled) return;
      setStats(s);
      // Both products' leads share this inbox, so the dashboard lists them together and marks each
      // row with a product dot. Only the ones still awaiting a decision are worth surfacing here.
      const list = Array.isArray(e) ? e.filter((x) => x.status === 'New') : [];
      list.sort((a, b) => new Date(b.created) - new Date(a.created));
      setEnquiries(list.slice(0, 4));
      setLoading(false);
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (loading) {
    return (
      <div className='adm-panel'>
        <Loader />
      </div>
    );
  }
  if (!stats) {
    return <div className='adm-empty'>Couldn't load the dashboard stats.</div>;
  }

  const apiAccounts = (stats.apiTrialCount || 0) + (stats.apiProductionCount || 0);
  const activeSubs = (stats.readerSubscribers || 0) + apiAccounts;

  const custTrend =
    stats.newCustomersThisMonth > 0
      ? { dir: 'up', text: `${stats.newCustomersThisMonth} this month` }
      : null;

  // Accounts-basis breakdown (distinct clients per product). Dual clients appear in both counts, so
  // this is shown with a "both" tail rather than as a sum of Total customers.
  const custSub =
    `${fmt(stats.readerAccounts)} App · ${fmt(stats.apiAccounts)} API` +
    (stats.dualAccounts > 0 ? ` · ${fmt(stats.dualAccounts)} both` : '');

  let callTrend = null;
  if (stats.apiCallsLastMonth > 0) {
    const pct = Math.round(
      ((stats.apiCallsThisMonth - stats.apiCallsLastMonth) / stats.apiCallsLastMonth) * 100
    );
    callTrend = { dir: pct < 0 ? 'down' : 'up', text: `${Math.abs(pct)}% vs last month` };
  }

  // Activity series for the hero chart + KPI sparkline.
  const series = (stats.apiCallsByDay || []).map((d) => ({
    date: d.date,
    label: fmtDay(d.date),
    count: d.count,
  }));
  const series30Total = series.reduce((sum, d) => sum + d.count, 0);
  const spark = series.slice(-14);

  // Trial vs production split (for the API card bar).
  const apiSplitTotal = apiAccounts || 1;
  const trialPct = Math.round(((stats.apiTrialCount || 0) / apiSplitTotal) * 100);

  // Reader "by plan" — the max count normalises the share bars.
  const readerMax = Math.max(1, ...(stats.readerByPlan || []).map((r) => r.count));

  // Trials in flight — the two products count differently: an App trial is time-boxed (Trialing plus
  // an end date), while an API trial is capped by call allowance and sits on the API Trial plan. Only
  // the App half has a deadline, so the footnote names it explicitly.
  const trialsInFlight = (stats.appTrials || 0) + (stats.apiTrialCount || 0);
  const trialSub =
    `${fmt(stats.appTrials)} App · ${fmt(stats.apiTrialCount)} API`;
  const trialTrend =
    stats.appTrialsEndingSoon > 0
      ? {
          dir: 'warn',
          text: `${stats.appTrialsEndingSoon} App ${
            stats.appTrialsEndingSoon === 1 ? 'trial ends' : 'trials end'
          } within 14 days`,
        }
      : null;

  const attention = [
    {
      value: stats.openEnquiries,
      label: 'Enquiries waiting for review',
      cta: 'Review now',
      to: link('enquiries'),
      tone: stats.openEnquiries > 0 ? 'err' : 'ok',
    },
    {
      value: stats.apiTrialCount,
      label: 'API trials not yet on Enterprise',
      cta: 'View accounts',
      to: `${link('customers')}?product=api`,
      tone: stats.apiTrialCount > 0 ? 'warn' : 'ok',
    },
    {
      value: stats.apiOverLimit,
      label: 'Customers over their call limit',
      cta: 'View usage',
      to: `${link('customers')}?product=api`,
      tone: stats.apiOverLimit > 0 ? 'err' : 'ok',
    },
  ];

  return (
    <section className='adm-section'>
      <div className='adm-overview__intro'>
        <p className='adm-sub' style={{ margin: 0 }}>
          A real-time view of customers and subscriptions across the RegulationCity App and Developer
          API.
        </p>
        <span className='adm-daterange'>
          <FaRegCalendarAlt /> This month ({monthRangeLabel()})
        </span>
      </div>

      {/* Top KPIs */}
      <div className='adm-kpis'>
        <Kpi
          icon={<FaUsers />}
          tone='teal'
          value={stats.totalAccounts}
          label='Total customers'
          sub={custSub}
          trend={custTrend}
        />
        <Kpi
          icon={<FaLayerGroup />}
          tone='violet'
          value={activeSubs}
          label='Active subscriptions'
          sub={`${fmt(stats.readerSubscribers)} RegulationCity App · ${fmt(apiAccounts)} Developer API`}
        />
        <Kpi
          icon={<FaCode />}
          tone='blue'
          value={stats.apiCallsThisMonth}
          label='API calls this month'
          trend={callTrend}
          spark={spark}
        />
        <Kpi
          icon={<FaHourglassHalf />}
          tone='amber'
          value={trialsInFlight}
          label='Trials in flight'
          sub={trialSub}
          trend={trialTrend}
        />
      </div>

      {/* Products — how each line of business is doing, ahead of any single-product detail */}
      <div className='adm-prodgrid'>
        <div className='adm-panel adm-prodcard'>
          <div className='adm-prodhead'>
            <span className='adm-prodhead__icon adm-prodhead__icon--reader'>
              <FaBookOpen />
            </span>
            <div>
              <div className='adm-prodhead__title'>RegulationCity App</div>
              <div className='adm-prodhead__desc'>The main web app (regulationcity.com/app)</div>
            </div>
          </div>
          <div className='adm-bignum'>{fmt(stats.readerSubscribers)}</div>
          <div className='adm-bignum__label'>active subscribers</div>
          <div className='adm-bd'>
            <div className='adm-bd__title'>Subscribers by plan</div>
            {stats.readerByPlan && stats.readerByPlan.length ? (
              stats.readerByPlan.map((r) => (
                <div className='adm-sharerow' key={r.plan}>
                  <div className='adm-sharerow__top'>
                    <span>{r.plan || '—'}</span>
                    <span className='adm-sharerow__count'>{fmt(r.count)}</span>
                  </div>
                  <div className='adm-bar'>
                    <div
                      className='adm-bar__fill adm-bar__fill--navy'
                      style={{ width: `${Math.max(4, (r.count / readerMax) * 100)}%` }}
                    />
                  </div>
                </div>
              ))
            ) : (
              <div className='adm-table__muted'>No active subscriptions.</div>
            )}
          </div>
          <div className='adm-bd'>
            <div className='adm-newstat'>
              <span className='adm-newstat__icon'>
                <FaArrowUp />
              </span>
              <div>
                <span className='adm-newstat__val'>{fmt(stats.readerNewThisMonth)}</span>
                <span className='adm-newstat__label'>new subscribers this month</span>
              </div>
            </div>
          </div>
          <div className='adm-prodcard__actions'>
            <Link className='adm-btn adm-btn--ghost' to={link('customers')}>
              View customers <FaArrowRight />
            </Link>
          </div>
        </div>

        <div className='adm-panel adm-prodcard'>
          <div className='adm-prodhead'>
            <span className='adm-prodhead__icon adm-prodhead__icon--api'>
              <FaCode />
            </span>
            <div>
              <div className='adm-prodhead__title'>Developer API</div>
              <div className='adm-prodhead__desc'>Programmatic access to RegulationCity’s data</div>
            </div>
          </div>
          <div className='adm-bignum'>{fmt(apiAccounts)}</div>
          <div className='adm-bignum__label'>API accounts</div>
          <div className='adm-minis adm-minis--top'>
            <MiniStat tone='green' label='Trials' value={stats.apiTrialCount} />
            <MiniStat tone='orange' label='Enterprise' value={stats.apiProductionCount} />
            <MiniStat tone='blue' label='Active keys' value={stats.apiActiveKeys} />
            <MiniStat tone='violet' label='Enquiries' value={stats.apiEnquiries} />
          </div>
          <div className='adm-bd'>
            <div className='adm-bd__title'>Trial vs Enterprise</div>
            <div className='adm-splitbar'>
              <div className='adm-splitbar__trial' style={{ width: `${trialPct}%` }} />
              <div className='adm-splitbar__prod' style={{ width: `${100 - trialPct}%` }} />
            </div>
            <div className='adm-splitlegend'>
              <span>
                <span className='adm-mini__dot adm-mini__dot--green' /> {fmt(stats.apiTrialCount)} trial
              </span>
              <span>
                <span className='adm-mini__dot adm-mini__dot--orange' /> {fmt(stats.apiProductionCount)}{' '}
                Enterprise
              </span>
            </div>
          </div>
          <div className='adm-prodcard__actions'>
            <Link className='adm-btn adm-btn--primary' to={link('enquiries')}>
              Review enquiries <FaArrowRight />
            </Link>
            <Link className='adm-btn adm-btn--ghost' to={`${link('customers')}?product=api`}>
              View API accounts <FaArrowRight />
            </Link>
          </div>
        </div>
      </div>

      {/* Act now — the two follow-up queues, side by side: what to chase, and who to chase it with */}
      <div className='adm-cols'>
        <div className='adm-panel'>
          <div className='adm-rowhead' style={{ marginBottom: '1rem' }}>
            <span className='adm-inline'>
              <FaBell style={{ color: '#e0a800' }} />
              <b>Needs attention</b>
            </span>
          </div>
          <div className='adm-attn'>
            {attention.map((a) => (
              <div className={`adm-attn__item adm-attn__item--${a.tone}`} key={a.label}>
                <div className={`adm-attn__num adm-attn__num--${a.tone}`}>{fmt(a.value)}</div>
                <div className='adm-attn__label'>{a.label}</div>
                <Link className='adm-attn__cta' to={a.to}>
                  {a.cta} <FaArrowRight />
                </Link>
              </div>
            ))}
          </div>
        </div>

        {stats.expiringSoon && stats.expiringSoon.length > 0 && (
          <div className='adm-panel adm-panel--flush adm-expiring'>
            <div className='adm-panel__head'>
              <span>Expiring soon</span>
              <span className='adm-expiring__hint'>Next 60 days</span>
            </div>
            <div>
              {stats.expiringSoon.map((e) => {
                const days = Math.max(0, Math.ceil((new Date(e.endDate).getTime() - Date.now()) / 86400000));
                const tone = days <= 7 ? 'is-soon' : days <= 30 ? 'is-near' : '';
                return (
                  <div className='adm-exprow' key={`${e.clientId}-${e.product}`}>
                    <span className={`adm-dot adm-dot--${e.product === 'App' ? 'reader' : 'api'}`} />
                    <div className='adm-exprow__main'>
                      <div className='adm-exprow__name'>{e.company || e.email || 'Account'}</div>
                      <div className='adm-exprow__sub'>
                        {e.product} · {e.isTrial ? 'Trial' : 'Enterprise'}
                      </div>
                    </div>
                    <div className={`adm-exprow__when ${tone}`}>
                      <span className='adm-exprow__date'>
                        {new Date(e.endDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </span>
                      <span className='adm-exprow__rel'>{e.isTrial ? 'Trial ends' : 'Renews'} in {days}d</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Detail — the numbers behind the summary above */}
      <div className='adm-cols'>
        <div className='adm-panel adm-chart'>
          <div className='adm-chart__head'>
            <div>
              {/* API-only by construction: the series counts ApiUsages rows, so it must not claim
                  to cover the reader app. */}
              <div className='adm-chart__eyebrow'>Developer API activity</div>
              <div className='adm-chart__value'>
                {fmt(series30Total)} <small>API calls · last 30 days</small>
              </div>
            </div>
          </div>
          <ActivityChart data={series} />
        </div>

        <div className='adm-panel adm-panel--flush'>
          <div className='adm-panel__head'>
            <b>Recent enquiries</b>
            <Link className='adm-link' to={link('enquiries')}>
              View all
            </Link>
          </div>
          {enquiries.length ? (
            <div>
              {/* A row list, not a table: in a half-width column the table clipped its own action. */}
              {enquiries.map((e) => (
                <div className='adm-enqrow' key={e.id}>
                  <span
                    className={`adm-dot adm-dot--${e.product === READER_APP ? 'reader' : 'api'}`}
                    title={e.product === READER_APP ? 'RegulationCity App' : 'Developer API'}
                  />
                  <div className='adm-enqrow__main'>
                    <div className='adm-enqrow__top'>
                      <span className='adm-enqrow__name'>
                        {`${e.firstName || ''} ${e.lastName || ''}`.trim() || e.email || '—'}
                      </span>
                      <span className='adm-enqrow__when'>{fmtDate(e.created)}</span>
                    </div>
                    <div className='adm-enqrow__msg'>{e.message || '—'}</div>
                  </div>
                  {e.email && (
                    <Link
                      className='adm-btn adm-btn--ghost adm-btn--sm'
                      to={`${link('customers')}?email=${encodeURIComponent(e.email)}`}
                    >
                      Look up
                    </Link>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className='adm-panel__body adm-table__muted'>Nothing waiting for review.</div>
          )}
        </div>
      </div>
    </section>
  );
};

export default connector(Overview);
